import React, { useState } from 'react';
import { useAppContext } from '../../../../Services/AppContext';
import useTranslation from '../../../../Hooks/useTranslation';
import apiClient from '../../../../Services/ApiClient';
import { NCZI_OID } from '../../Enums/ncziOid.enum';
import Button from '../../../../Components/Button/Button';
import { Theme } from '../../../../Enums/Theme.enum';
import {
	TermItem,
	useLazyMetaQuery,
	useLazyMyMetaQuery,
	useLazyTermItemsQuery,
} from '../../../../graphql/graphql';
import { useEffectAsync } from '../../../../helpers';
import Loading from '../../../../Components/Loading';
import NZCIListItem from '../Base/NZCIListItem';
import Text from '../../../../Components/Base/Text/Text';
import NCZISetupGeneralInformationModal from './NCZISetupGeneralInformation/NCZISetupGeneralInformationModal';
import NCZISetupInsuranceRow from './NCZISetupInsuranceRow/NCZISetupInsuranceRow';
import { NcziSetupSettings } from './NcziSetupSettings';
import { NCZISetupInsuranceCompanyValues } from './NCZISetupInsuranceCompany/NCZISetupInsuranceCompany';
import NCZISetupExtensionValue from './NCZISetupExtensionValue/NCZISetupExtensionValue';
import useNotificationHook from '../../../../Hooks/useNotification.hook';
import { NCZISetupGeneralInformationData } from './NCZISetupGeneralInformation/NCZISetupGeneralInformation';
import styles from './NCZISetupModalContent.module.scss';
import useRootWorkplace from '../../../Manager/Hooks/useRootWorkplace';
import { WorkplaceInsuranceSettings } from '../../Interfaces/insuranceSettings';

interface Props {
	onClose?: () => void;
	showFinishButton?: boolean;
}

const NCZISetupModalContent: React.FC<Props> = ({
	onClose,
	showFinishButton = false,
}) => {
	const [isInitialized, setIsInitialized] = useState(false);
	const [insuranceCompanies, setInsuranceCompanies] = useState<TermItem[]>([]);
	const [userSetup, setUserSetup] = useState<NcziSetupSettings | null>(null);
	const [workplaceInsurance, setWorkplaceInsurance] =
		useState<WorkplaceInsuranceSettings>({});
	const { workplaceId, userData } = useAppContext();
	const { t } = useTranslation();
	const [getMeta] = useLazyMetaQuery();
	const [getMyMeta] = useLazyMyMetaQuery();
	const [isGeneralInformationSetupOpen, setIsGeneralInformationSetupOpen] =
		useState(false);
	const [getTermItems] = useLazyTermItemsQuery();
	const { showNotificationSuccess } = useNotificationHook();
	const { rootWorkplaceId } = useRootWorkplace(workplaceId, userData.id);

	const saveSettings = async (settings: NcziSetupSettings) => {
		await apiClient.post(`/user/${userData?.id}/meta`, {
			key: 'nczi',
			value: settings,
		});

		showNotificationSuccess({ content: t('component.nczi.savedSuccessfully') });
	};

	useEffectAsync(async () => {
		if (!rootWorkplaceId) {
			return;
		}

		const { me } = await getMyMeta().unwrap();
		for (const meta of me?.meta ?? []) {
			if (meta?.key === 'nczi') {
				setUserSetup(meta.value);
			}
		}

		const { termItems } = await getTermItems({
			termType: 'insurance_companies',
		}).unwrap();
		if (termItems?.data || []) {
			const tmpTermItems = [];
			for (const termItem of termItems?.data || []) {
				if (termItem) {
					tmpTermItems.push(termItem);
				}
			}
			setInsuranceCompanies(tmpTermItems);
		}

		const { meta: workplaceMeta } = await getMeta({
			key: 'nczi',
			workplaceId: rootWorkplaceId,
		}).unwrap();

		if (workplaceMeta?.value) {
			setWorkplaceInsurance(workplaceMeta.value.insurance || {});
		}

		setIsInitialized(true);
	}, [rootWorkplaceId]);

	const handleSaveInsurance = async (
		insuranceCompanyKey: string,
		values: NCZISetupInsuranceCompanyValues,
	) => {
		const tmpInsuranceSettings = { ...userSetup!.insuranceSettings };

		for (const insuranceCompany of insuranceCompanies) {
			const insuranceKey = insuranceCompany.value3 || 'default';
			if (typeof tmpInsuranceSettings[insuranceKey] === 'undefined') {
				tmpInsuranceSettings[insuranceKey] = null;
			}
		}

		tmpInsuranceSettings[insuranceCompanyKey] = values;
		const newSettings = {
			...userSetup!,
			insuranceSettings: tmpInsuranceSettings,
		} as NcziSetupSettings;
		setUserSetup(newSettings);
		await saveSettings(newSettings);
	};

	const handleSaveGeneralInformation = async (
		data: NCZISetupGeneralInformationData,
	) => {
		setIsGeneralInformationSetupOpen(false);
		setIsInitialized(false);

		const newSettings = {
			...data,
			insuranceSettings: userSetup?.insuranceSettings || {},
		};
		setUserSetup(newSettings);
		await saveSettings(newSettings);

		setIsInitialized(true);
	};

	if (!isInitialized) {
		return <Loading />;
	}

	return (
		<div className={styles.wrapper}>
			<div className={styles.item}>
				<NZCIListItem
					title='component.nczi.setup.healthProfessional'
					value={
						<NCZISetupExtensionValue
							extension={{
								code: userSetup?.healthProfessionalCode || '-',
								scheme: {
									oid: NCZI_OID.HEALTH_PROFESSIONAL,
								},
								valid: undefined,
							}}
						/>
					}
					extra={userSetup?.healthProfessionalCode}
				/>
				<NZCIListItem
					title='component.nczi.setup.oupzs'
					value={
						<NCZISetupExtensionValue
							extension={userSetup?.healthServiceProvider || null}
						/>
					}
					extra={userSetup?.healthServiceProvider?.code}
				/>
				<NZCIListItem
					title='component.nczi.setup.specialization'
					value={
						userSetup
							? userSetup?.oupzsZprCode +
							  ' - ' +
							  userSetup?.specialization?.displayName
							: ''
					}
					extra={userSetup?.specialization?.code}
				/>
				<NZCIListItem
					title='component.nczi.setup.serviceProvider'
					value={
						<NCZISetupExtensionValue
							extension={userSetup?.serviceProvider || null}
						/>
					}
					extra={userSetup?.serviceProvider?.code}
				/>
				<Button
					theme={Theme.BLUE}
					title={userSetup ? 'base.change' : 'component.nczi.setup.startSetup'}
					className={styles.button}
					onClick={() => setIsGeneralInformationSetupOpen(true)}
				/>
			</div>
			{userSetup && (
				<div className={styles.item}>
					<Text tx='component.nczi.setup.insuranceSetup' />
					{insuranceCompanies.map((insuranceCompany, i) => (
						<NCZISetupInsuranceRow
							key={i}
							insuranceCompany={insuranceCompany}
							userSettings={userSetup}
							workplaceInsurance={workplaceInsurance}
							saveInsuranceSettings={values =>
								handleSaveInsurance(
									insuranceCompany.value3 || 'default',
									values,
								)
							}
						/>
					))}
				</div>
			)}
			{userSetup && showFinishButton && (
				<div className={styles.item}>
					<Button
						theme={Theme.BLUE}
						title={'base.saveSettings'}
						className={styles.button}
						onClick={() => onClose?.()}
					/>
				</div>
			)}
			<NCZISetupGeneralInformationModal
				isOpen={isGeneralInformationSetupOpen}
				onClose={() => setIsGeneralInformationSetupOpen(false)}
				onSuccess={handleSaveGeneralInformation}
			/>
		</div>
	);
};

export default NCZISetupModalContent;
